import { useEffect, useState } from "react";

type IUseDragReorderReturn = {
    mousePos: IPosition;
    dropIdx: number;
};

export interface IUnique<T> {
    id: T;
}

interface IUseDragReorderProps<T> {
    enabled: boolean;
    items: IUnique<T>[];
    draggedIdx: number | null;
    dropZoneClassName: string;
    onDrop: ((id?: T, idx?: number) => void) | null;
    mouseHandlerComponentClassName?: string;
    isVertical?: boolean;
}

export interface IPosition {
    x: number;
    y: number;
    top: number;
    left: number;
}

// some common handlers needed for allowing click drag reorder
// taken from: https://dev.to/h8moss/build-a-reorderable-list-in-react-29on
export function useDragReorder<T>(props: IUseDragReorderProps<T>): IUseDragReorderReturn {
    const { items, draggedIdx, enabled, dropZoneClassName, onDrop, mouseHandlerComponentClassName, isVertical = true } = props;
    const isDragging = draggedIdx !== null && draggedIdx !== -1;
    const [mousePos, setMousePos] = useState<IPosition>({ x: 0, y: 0, top: 0, left: 0 });
    const [dropIdx, setDropIdx] = useState<number>(0);
    const mouseHandlerComponent = document.getElementsByClassName(mouseHandlerComponentClassName ?? "").item(0);

    // listen to mouse position if draggable on
    useEffect(() => {
        const mouseMoveHandler = (e: MouseEvent) => {
            setMousePos({ 
                x: e.x,
                y: e.y,
                left: mouseHandlerComponent === null ? e.x : e.x - mouseHandlerComponent.getBoundingClientRect().left,
                top: mouseHandlerComponent === null ? e.y : e.y - mouseHandlerComponent.getBoundingClientRect().top,
            });
        };

        const mouseUpHandler = (_: MouseEvent) => {
            document.body.style.cursor = "default";

            if (onDrop) {
                let idxToUse = dropIdx;
                onDrop(items[idxToUse]?.id, idxToUse);
            }
        };

        if (enabled) {
            mouseHandlerComponent !== null  
                ? mouseHandlerComponent.addEventListener("mousemove", mouseMoveHandler)
                : document.addEventListener("mousemove", mouseMoveHandler);
            
            document.addEventListener("mouseup", mouseUpHandler);
        }

        return () => {
            mouseHandlerComponent !== null  
                ? mouseHandlerComponent.removeEventListener("mousemove", mouseMoveHandler)
                : document.removeEventListener("mousemove", mouseMoveHandler);
            
            document.removeEventListener("mouseup", mouseUpHandler);
        };
    }, [isDragging, dropIdx, onDrop, items, enabled]);

    const dropItemVertically = (draggedIdx: number, elements: Element[]) => {
        // get all drop-zones' y-axis position
        const positions = elements.map((e) => {
            return e.getBoundingClientRect().top;
        });
    
        // get the difference with the mouse's y position
        const absDifferences = positions.map((v) => Math.abs(v - mousePos.y));
    
        // get the item closest to the mouse
        let result = absDifferences.indexOf(Math.min(...absDifferences));
    
        if (result > draggedIdx) result += 1;
    
        setDropIdx(result);
    }

    const dropItemHorizontally = (draggedIdx: number, elements: Element[]) => {
        // get all drop-zones' x-axis position
        const positions = elements.map((e) => {
            return e.getBoundingClientRect().left;
        });

        // get the difference with the mouse's x position
        const absDifferences = positions.map((v) => Math.abs(v - mousePos.x));

        // get the item closest to the mouse
        let result = absDifferences.indexOf(Math.min(...absDifferences));

        if (result > draggedIdx) result += 1;

        setDropIdx(result);
    }

    // get closest drop zone to mouse position so we only show that one
    // https://dev.to/h8moss/build-a-reorderable-list-in-react-29on
    useEffect(() => {
        if (isDragging) {
            // get all drop-zones
            const elements = Array.from(document.getElementsByClassName(dropZoneClassName));

            if(isVertical) {
                dropItemVertically(draggedIdx, elements);
            }
            else {
                dropItemHorizontally(draggedIdx, elements);
            }
        }
    }, [isDragging, mousePos, items, draggedIdx, dropZoneClassName]);

    return {
        mousePos,
        dropIdx,
    };
}

export function dropItemForward<TItems>(items: TItems[], startPosition: number, endPosition: number): TItems[] {
    let newItems = [...items];

    const temp = newItems[startPosition];

    for (let i = startPosition; i < endPosition; i++) {
        newItems[i] = newItems[i + 1];
    }

    newItems[endPosition - 1] = temp;

    return newItems;
}

export function dropItemBackward<TItems>(items: TItems[], startPosition: number, endPosition: number): TItems[] {
    const newItems = [...items];

    const temp = newItems[startPosition];

    for (let i = startPosition; i > endPosition; i--) {
        newItems[i] = newItems[i - 1];
    }

    newItems[endPosition] = temp;

    return newItems;
}
